import { mutationOptions, UseMutationOptions } from "@tanstack/react-query";
import { api } from "services/restUtil";

type DefineApiMutationProps<TResponse, TVariables> = {
  path: string | ((payload: TVariables) => string);
  mutationFn?: (path: string, payload: TVariables) => Promise<TResponse>;
} & Omit<UseMutationOptions<TResponse, Error, TVariables>, "mutationFn" | "mutationKey">;

/**
 * Shared config for Talaera mutation factories.
 *
 * `path` is both the stable mutation-key prefix and the request path source.
 * It can be a static string or a function that derives the path from the
 * mutation payload, which is useful when route params live inside `variables`.
 *
 * If `mutationFn` is omitted, the helper defaults to `api.post(path, payload)`.
 * Provide a custom `mutationFn` when the request method is not `POST` or when
 * the request body needs to differ from the original mutation payload.
 */
export function defineApiMutation<TResponse = void, TVariables = void>(
  props: DefineApiMutationProps<TResponse, TVariables>
) {
  const defaultMutationFn = (path: string, variables: TVariables) =>
    api.post<TVariables, TResponse>(path, variables);

  /**
   * Creates mutation options with Talaera's standard `[path]` mutation key shape.
   *
   * Custom mutation functions receive the resolved request path plus the original
   * payload. The helper does not transform the payload automatically.
   */
  return function getMutation() {
    const { mutationFn = defaultMutationFn, ...rest } = props;

    return mutationOptions({
      mutationKey: [props.path],
      mutationFn: (variables: TVariables) => {
        const path =
          typeof props.path === "function" ? props.path(variables) : props.path;
        return mutationFn(path, variables);
      },
      ...rest,
    });
  };
}
